import { Component, Input, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NgbCollapse } from '@ng-bootstrap/ng-bootstrap';

// service
import { AuthenticationService } from 'src/app/core/service/auth.service';
import { EventService } from 'src/app/core/service/event.service';

// utility
import { changeBodyAttribute, findAllParent, findMenuItem } from '../helper/utils';

// types
import { User } from 'src/app/core/models/auth.models';
import { MenuItem } from '../models/menu.model';

// data
import { MENU_ITEMS } from 'src/app/pages/menu';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-left-sidebar',
  templateUrl: './left-sidebar.component.html',
  styleUrls: ['./left-sidebar.component.scss']
})
export class LeftSidebarComponent implements OnInit {

  @Input() includeUserProfile: boolean = false;

  leftSidebarClass = 'sidebar-enable';
  activeMenuItems: string[] = [];
  loggedInUser: any | null = {};
  menuItems: MenuItem[] = [];
  rolesSubscription: Subscription;
  permissions: string[] = [];
  logoClub: string = localStorage.getItem('imageLogo');
  lastOpenMenuItems: string[] = [];


  constructor(
    router: Router,
    private authService: AuthenticationService,
    private eventService: EventService) {
    router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        this._activateMenu(); //actiavtes menu
        this.hideMenu(); //hides leftbar on change of route
      }
    });

    this.rolesSubscription = this.authService.getRolesObservable().subscribe((roles) => {
      if (roles) {
        this.permissions = roles;
        this.initMenu();
      }
    });


  }

  ngOnInit(): void {
    this.initMenu();
    this.loggedInUser = this.authService.currentUser();
  }

  ngOnChanges(): void {
    if (this.includeUserProfile) {
      changeBodyAttribute('data-sidebar-user', 'true');
    }
    else {
      changeBodyAttribute('data-sidebar-user', 'false');
    }
  }

  /**
   * On view init - activating menuitems
   */
  ngAfterViewInit() {
    setTimeout(() => {
      this._activateMenu();
    });
  }
  /**
   * initialize menuitems
   */
  initMenu() {
    MENU_ITEMS.forEach(menuItem => {
      this.setMenuItemVisibility(menuItem);
    });
    this.menuItems = MENU_ITEMS;
    this.updateSectionVisibility();
  }

  setMenuItemVisibility(menuItem: MenuItem) {
    if (menuItem.children) {
      menuItem.children.forEach((child: MenuItem) => {
        this.setMenuItemVisibility(child);
      });
    } else {
      menuItem.show = this.permissions.includes(menuItem.role!);
    }
  }

  checkChildrenShow(menuItem: MenuItem): boolean {
    if (menuItem.children) {
      return menuItem.children.some((child: MenuItem) => {
        return this.checkChildrenShow(child);
      });
    } else {
      return menuItem.show;
    }
  }

  updateSectionVisibility(): void {
    for (let i = 0; i < this.menuItems.length; i++) {
      const menuItem = this.menuItems[i];
      if (!menuItem.isTitle) {
        continue;
      }

      let show = false;
      for (let j = i + 1; j < this.menuItems.length; j++) {
        const nextItem = this.menuItems[j];
        if (nextItem.isTitle) {
          break;
        }

        if (this.checkChildrenShow(nextItem)) {
          show = true;
          break;
        }
      }

      menuItem.show = show;
    }
  }

  /**
   * Returns true or false if given menu item has child or not
   * @param item menuItem
   */
  hasSubmenu(menu: MenuItem): boolean {
    return menu.children ? true : false;
  }

  findSiblingMenus(target: MenuItem, menuItems: MenuItem[] = this.menuItems): MenuItem[] {
    for (const menu of menuItems) {
      if (menu.key === target.key) {
        return menuItems;
      }

      if (menu.children?.length) {
        const siblingGroup = this.findSiblingMenus(target, menu.children);
        if (siblingGroup.length) {
          return siblingGroup;
        }
      }
    }

    return [];
  }

  updateCollapsedState(menuItems: MenuItem[], openMenuItems: string[]): void {
    menuItems.forEach((menu: MenuItem) => {
      if (menu.children?.length) {
        menu.collapsed = !openMenuItems.includes(menu.key!);
        this.updateCollapsedState(menu.children, openMenuItems);
      }
    });
  }

  openMenuPath(menuItem: MenuItem): void {
    const matchingObjs = [menuItem['key'], ...findAllParent(this.menuItems, menuItem)];
    this.activeMenuItems = matchingObjs;
    this.lastOpenMenuItems = matchingObjs;
    this.updateCollapsedState(this.menuItems, matchingObjs);
  }

  applyOpenMenuKeys(openMenuItems: string[]): void {
    if (!openMenuItems.length) {
      return;
    }

    this.activeMenuItems = openMenuItems;
    this.lastOpenMenuItems = openMenuItems;
    this.updateCollapsedState(this.menuItems, openMenuItems);
  }

  findMenuItemByUrl(currentPath: string, menuItems: MenuItem[] = this.menuItems): MenuItem | null {
    let bestMatch: MenuItem | null = null;

    for (const menu of menuItems) {
      if (menu.children?.length) {
        const childMatch = this.findMenuItemByUrl(currentPath, menu.children);
        if (childMatch) {
          bestMatch = childMatch;
        }
      } else if (menu.url) {
        const menuPath = menu.url.startsWith('/') ? menu.url : `/${menu.url}`;
        const isExactMatch = currentPath === menuPath;
        const isNestedMatch = currentPath.startsWith(`${menuPath}/`);

        if (isExactMatch || isNestedMatch) {
          if (!bestMatch || menuPath.length > (bestMatch.url?.length || 0)) {
            bestMatch = menu;
          }
        }
      }
    }

    return bestMatch;
  }

  /**
   * activates menu
   */
  _activateMenu(): void {
    const currentPath = window.location.pathname;
    const activeMt = this.findMenuItemByUrl(currentPath);
    if (activeMt) {
      this.openMenuPath(activeMt);
      return;
    }

    this.applyOpenMenuKeys(this.lastOpenMenuItems);
  }

  /**
   * toggles open menu
   * @param menuItem clicked menuitem
   * @param collapse collpase instance
   */
  toggleMenuItem(menuItem: MenuItem, collapse: NgbCollapse): void {
    menuItem.collapsed = !menuItem.collapsed;

    if (!menuItem.collapsed) {
      const siblingMenus = this.findSiblingMenus(menuItem);
      siblingMenus.forEach((menu: MenuItem) => {
        if (menu.key !== menuItem.key) {
          menu.collapsed = true;
        }
      });
    }
  }

  /**
   * Hides the menubar
   */
  hideMenu() {
    document.body.classList.remove('sidebar-enable');
  }
}
